import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getEvent, submitAvailability, updateAvailability, getParticipantAvailability, getResults } from '../api';
import Calendar from '../components/Calendar';
import TimeSlots from '../components/TimeSlots';
import Tooltip from '../components/Tooltip';

function formatDisplayDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function formatDuration(minutes) {
  if (!minutes && minutes !== 0) return '';
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `${h}hr${h !== 1 ? 's' : ''}`;
  return `${h}hr${h !== 1 ? 's' : ''} ${m}min`;
}

// Copy link button component
function CopyLinkButton() {
  const [copied, setCopied] = useState(false);
  const url = window.location.href;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      type="button"
      className="btn-secondary"
      onClick={handleCopy}
      style={{ padding: '6px 14px', fontSize: '0.8rem' }}
    >
      {copied ? '✓ Copied!' : '🔗 Copy Link'}
    </button>
  );
}

// Confirmation modal component
function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '24px',
    }}>
      <div className="card card-glow" style={{
        maxWidth: '420px', width: '100%', textAlign: 'center',
        animation: 'fadeInUp 0.2s ease forwards',
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '16px' }}>⚠️</div>
        <p style={{ fontSize: '1.05rem', marginBottom: '24px', lineHeight: '1.5' }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button type="button" className="btn-secondary" onClick={onCancel} style={{ minWidth: '120px' }}>
            Go Back
          </button>
          <button type="button" className="btn-primary" onClick={onConfirm} style={{ minWidth: '120px' }}>
            Yes, Submit
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EventPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Submission state — pre-fill name from ?name= query param if present
  const [participantName, setParticipantName] = useState(searchParams.get('name') || '');
  const [participantEmail, setParticipantEmail] = useState('');
  const [anyTime, setAnyTime] = useState(false);
  const [slots, setSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Confirmation modal state
  const [showConfirm, setShowConfirm] = useState(false);

  // Edit mode state
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Results state
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);

  const loadEvent = useCallback(async () => {
    try {
      const data = await getEvent(id);
      setEvent(data);

      if (data.slots_filled >= data.slots_needed) {
        setShowResults(true);
        setResultsLoading(true);
        try {
          const r = await getResults(id);
          setResults(r);
        } finally {
          setResultsLoading(false);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  async function handleEditParticipant(participant) {
    setEditLoading(true);
    setSubmitError('');
    try {
      const data = await getParticipantAvailability(id, participant.id);
      setEditingParticipant({ id: participant.id, name: participant.name });
      setParticipantName(participant.name);
      setAnyTime(!!data.participant.any_time);
      setSlots(data.slots.sort((a, b) => a.date.localeCompare(b.date)));
      setSubmitted(false);
      setShowResults(false);
    } catch (err) {
      setSubmitError('Failed to load availability for editing: ' + err.message);
    } finally {
      setEditLoading(false);
    }
  }

  function handleCancelEdit() {
    setEditingParticipant(null);
    setParticipantName('');
    setParticipantEmail('');
    setAnyTime(false);
    setSlots([]);
    setSubmitError('');
    // Go back to results if all filled
    if (event && event.slots_filled >= event.slots_needed) {
      setShowResults(true);
      handleViewResults();
    }
  }

  function handleToggleDate(dateStr) {
    const existingIdx = slots.findIndex(s => s.date === dateStr);
    if (existingIdx >= 0) {
      setSlots(s => s.filter((_, i) => i !== existingIdx));
    } else {
      setSlots(s => [...s, { date: dateStr, start_time: '09:00', end_time: '17:00' }].sort((a, b) => a.date.localeCompare(b.date)));
    }
  }

  function handleUpdateSlot(index, field, value) {
    setSlots(s => s.map((slot, i) => i === index ? { ...slot, [field]: value } : slot));
  }

  function handleRemoveSlot(index) {
    setSlots(s => s.filter((_, i) => i !== index));
  }

  function handleSubmitClick(e) {
    e.preventDefault();
    setSubmitError('');

    if (!participantName.trim()) {
      setSubmitError('Please enter your name.');
      return;
    }
    if (!anyTime && slots.length === 0) {
      setSubmitError('Please select at least one date, or choose "I\'m available any time."');
      return;
    }

    if (!anyTime) {
      for (const slot of slots) {
        if (slot.start_time >= slot.end_time) {
          setSubmitError(`Invalid time range on ${slot.date}: start time must be before end time.`);
          return;
        }
      }
    }

    setShowConfirm(true);
  }

  async function handleConfirmedSubmit() {
    setShowConfirm(false);
    setSubmitting(true);

    try {
      if (editingParticipant) {
        await updateAvailability(id, editingParticipant.id, { slots: anyTime ? [] : slots, any_time: anyTime });
        setEditingParticipant(null);
      } else {
        const response = await submitAvailability(id, {
          participant_name: participantName.trim(),
          participant_email: participantEmail.trim() || undefined,
          any_time: anyTime,
          slots: anyTime ? [] : slots,
        });

        // Don't auto-show results — let the confirmation screen handle messaging
      }

      setSubmitted(true);

      const updatedEvent = await getEvent(id);
      setEvent(updatedEvent);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleViewResults() {
    setShowResults(true);
    setResultsLoading(true);
    try {
      const r = await getResults(id);
      setResults(r);
    } catch (err) {
      setError('Failed to load results.');
    } finally {
      setResultsLoading(false);
    }
  }

  function handleBackToEvent() {
    setSubmitted(false);
    setParticipantName('');
    setParticipantEmail('');
    setAnyTime(false);
    setSlots([]);
    setSubmitError('');
  }

  if (loading) {
    return (
      <div className="container" style={{ marginTop: '80px', textAlign: 'center' }}>
        <div className="card">
          <p style={{ color: 'var(--text-muted)' }}>Loading event...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ marginTop: '80px', textAlign: 'center' }}>
        <div className="card">
          <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>🔍</div>
          <h2>Event Not Found</h2>
          <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>{error}</p>
        </div>
      </div>
    );
  }

  const allFilled = event.slots_filled >= event.slots_needed;
  const progressPct = Math.min(100, Math.round((event.slots_filled / event.slots_needed) * 100));

  // --- Results View ---
  if (showResults && (results || resultsLoading)) {
    if (resultsLoading) {
      return (
        <div className="container" style={{ marginTop: '80px', textAlign: 'center' }}>
          <div className="card">
            <p style={{ color: 'var(--text-muted)' }}>Computing results...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="container" style={{ marginTop: '40px' }}>
        {showConfirm && (
          <ConfirmModal
            message="Are you sure you want to submit? Make sure you've reviewed your dates and times."
            onConfirm={handleConfirmedSubmit}
            onCancel={() => setShowConfirm(false)}
          />
        )}

        <div className="fade-in" style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: '1.8rem' }}>{event.name}</h1>
            <span className="badge badge-success">Results</span>
            <CopyLinkButton />
          </div>
          {event.description && <p style={{ color: 'var(--text-secondary)' }}>{event.description}</p>}
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '8px' }}>
            Organized by {event.organizer_name} · {results.participants.length} participants
          </p>
        </div>

        <div className="card fade-in fade-in-delay-1" style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '1.2rem', marginBottom: '4px' }}>Participants</h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '12px' }}>
            Click a name to edit their availability
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {results.participants.map(p => (
              <span
                key={p.id}
                className="badge badge-accent"
                style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                onClick={() => handleEditParticipant(p)}
                onMouseEnter={e => { e.target.style.background = 'var(--accent)'; e.target.style.color = '#fff'; }}
                onMouseLeave={e => { e.target.style.background = ''; e.target.style.color = ''; }}
                title={`Click to edit ${p.name}'s availability`}
              >
                {editLoading ? '...' : `✏️ ${p.name}`}
              </span>
            ))}
          </div>
        </div>

        <div className="card fade-in fade-in-delay-2">
          {(() => {
            const allAnyTime = results.participants.length > 0 && results.participants.every(p => p.any_time);

            if (allAnyTime) {
              return (
                <div style={{ textAlign: 'center', padding: '32px 24px' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🎉</div>
                  <p style={{ color: 'var(--success)', fontSize: '1.2rem', fontWeight: 600, marginBottom: '8px' }}>
                    Everyone is available any time!
                  </p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>
                    All participants indicated they're flexible — pick whatever works best.
                  </p>
                </div>
              );
            }

            return (
              <>
                <h2 style={{ fontSize: '1.2rem', marginBottom: '16px' }}>
                  {results.best_dates.length > 0 ? '🏆 Best Available Times' : 'Results'}
                </h2>

                {results.best_dates.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px' }}>
                    <p style={{ color: 'var(--danger)', fontSize: '1.1rem' }}>No overlapping times found</p>
                    <p style={{ color: 'var(--text-muted)', marginTop: '8px' }}>
                      The participants' availability didn't overlap on any date.
                    </p>
                  </div>
                ) : (
                  results.best_dates.slice(0, 10).map((bd, i) => (
                    <div key={i} className={`result-row ${i === 0 ? 'best' : ''}`}>
                      <div className="result-rank">#{i + 1}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                          {formatDisplayDate(bd.date)}
                        </div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.9rem', color: 'var(--accent-light)' }}>
                          {formatTime(bd.start_time)} – {formatTime(bd.end_time)}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {formatDuration(bd.duration_minutes)} available
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.2rem', fontWeight: 700, color: i === 0 ? 'var(--success)' : 'var(--text-primary)' }}>
                          {bd.participant_count}/{results.participants.length}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                          {bd.participants.join(', ')}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </>
            );
          })()}
        </div>

        {/* Per-participant breakdown */}
        {results.participants && results.participants.length > 0 && (
          <div className="card fade-in fade-in-delay-3" style={{ marginTop: '24px' }}>
            <h2 style={{ fontSize: '1.2rem', marginBottom: '16px' }}>📋 Individual Availability</h2>
            {results.participants.map(p => {
              const avails = (results.participant_availability && results.participant_availability[p.name]) || [];
              const isAnyTime = !!p.any_time;
              return (
                <div key={p.id} style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '0.95rem', color: 'var(--accent-light)', marginBottom: '8px' }}>{p.name}</h3>
                  {isAnyTime ? (
                    <span style={{
                      padding: '6px 14px',
                      background: 'rgba(0, 206, 201, 0.1)',
                      border: '1px solid rgba(0, 206, 201, 0.3)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.85rem',
                      color: 'var(--success)',
                      fontWeight: 600,
                    }}>
                      ✓ Available any time
                    </span>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {avails.map((a, i) => (
                        <span key={i} style={{
                          padding: '4px 10px',
                          background: 'var(--bg-input)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.8rem',
                          fontFamily: 'var(--font-mono)',
                          color: 'var(--text-secondary)',
                        }}>
                          {new Date(a.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          {' '}{formatTime(a.start_time)}–{formatTime(a.end_time)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // --- Submitted confirmation ---
  if (submitted && !editingParticipant) {
    return (
      <div className="container" style={{ marginTop: '60px' }}>
        <div className="card card-glow fade-in" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>✓</div>
          <h2 style={{ marginBottom: '8px' }}>Availability Submitted</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
            Thanks, {participantName}! Your availability has been recorded.
          </p>

          <div className="progress-bar" style={{ maxWidth: '300px', margin: '0 auto 12px' }}>
            <div className="progress-bar-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            {event.slots_filled} / {event.slots_needed} participants responded
          </p>

          {allFilled ? (
            <div style={{ marginTop: '24px' }}>
              <p style={{ color: 'var(--success)', fontWeight: 600, marginBottom: '4px' }}>
                🎉 You were the last one!
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                All participants are in — {event.organizer_name} has been notified with the results.
              </p>
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '16px' }}>
              The organizer will be emailed once everyone responds.
            </p>
          )}

          <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn-secondary" onClick={handleBackToEvent} style={{ padding: '8px 16px', fontSize: '0.85rem' }}>
              ← Back to Event
            </button>
            <CopyLinkButton />
          </div>
        </div>
      </div>
    );
  }

  // --- All slots filled, waiting for results (but not editing) ---
  if (allFilled && !editingParticipant) {
    return (
      <div className="container" style={{ marginTop: '60px' }}>
        <div className="card fade-in" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>🎉</div>
          <h2 style={{ marginBottom: '8px' }}>{event.name}</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
            All {event.slots_needed} participants have responded!
          </p>
          <button className="btn-primary" onClick={handleViewResults}>
            View Results
          </button>
        </div>
      </div>
    );
  }

  // --- Main availability submission / edit form ---
  const selectedDates = slots.map(s => s.date);
  const isEditing = editingParticipant !== null;

  return (
    <div className="container" style={{ marginTop: '40px' }}>
      {showConfirm && (
        <ConfirmModal
          message={isEditing
            ? anyTime
              ? `Update ${editingParticipant.name} to be available any time? This will replace their previous date selections.`
              : `Are you done editing ${editingParticipant.name}'s availability? This will overwrite their previous submission.`
            : anyTime
              ? "You're saying you're available for any date and time. Is that correct?"
              : `Are you done? You're about to submit availability for ${slots.length} date${slots.length !== 1 ? 's' : ''}. Make sure your times are correct.`
          }
          onConfirm={handleConfirmedSubmit}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      <div className="fade-in" style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <h1 style={{ fontSize: '1.8rem' }}>{event.name}</h1>
          {isEditing ? (
            <span className="badge badge-success">Editing</span>
          ) : (
            <span className="badge badge-accent">
              {event.slots_filled}/{event.slots_needed} responded
            </span>
          )}
          <CopyLinkButton />
        </div>
        {isEditing && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(0, 206, 201, 0.1)',
            border: '1px solid rgba(0, 206, 201, 0.3)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '8px',
          }}>
            <span style={{ color: 'var(--success)', fontSize: '0.9rem' }}>
              ✏️ Editing availability for <strong>{editingParticipant.name}</strong>
            </span>
            <button type="button" className="btn-secondary" onClick={handleCancelEdit} style={{ padding: '6px 14px', fontSize: '0.8rem' }}>
              Cancel Edit
            </button>
          </div>
        )}
        {event.description && (
          <p style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>{event.description}</p>
        )}
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          Organized by {event.organizer_name}
        </p>

        {!isEditing && (
          <div className="progress-bar" style={{ marginTop: '16px' }}>
            <div className="progress-bar-fill" style={{ width: `${progressPct}%` }} />
          </div>
        )}
      </div>

      {event.participants.length > 0 && (
        <div style={{ marginBottom: '24px' }} className="fade-in fade-in-delay-1">
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '8px' }}>
            Already responded (click to edit):
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {event.participants.map(p => (
              <span
                key={p.id}
                className="badge badge-accent"
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  outline: editingParticipant?.id === p.id ? '2px solid var(--success)' : 'none',
                }}
                onClick={() => handleEditParticipant(p)}
                onMouseEnter={e => { e.target.style.background = 'var(--accent)'; e.target.style.color = '#fff'; }}
                onMouseLeave={e => { e.target.style.background = ''; e.target.style.color = ''; }}
                title={`Click to edit ${p.name}'s availability`}
              >
                ✏️ {p.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmitClick}>
        {!isEditing && (
          <div className="card fade-in fade-in-delay-1" style={{ marginBottom: '20px' }}>
            <div className="field">
              <label htmlFor="participant-name">Your Name <Tooltip text="Enter your name so the organizer knows who submitted this availability" /></label>
              <input
                id="participant-name"
                type="text"
                placeholder="Enter your name"
                value={participantName}
                onChange={e => setParticipantName(e.target.value)}
                required
                maxLength={100}
              />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="participant-email">Your Email <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--text-muted)' }}>(optional)</span> <Tooltip text="If you'd like to receive a copy of the final results by email, enter your address here" /></label>
              <input
                id="participant-email"
                type="email"
                placeholder="you@example.com"
                value={participantEmail}
                onChange={e => setParticipantEmail(e.target.value)}
              />
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                Get a copy of the results when they're ready
              </p>
            </div>
          </div>
        )}

        <div className="card fade-in fade-in-delay-2" style={{ marginBottom: '20px' }}>
          <label htmlFor="any-time" style={{
            display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
            textTransform: 'none', letterSpacing: 0, fontSize: '1rem', fontWeight: 500,
            color: anyTime ? 'var(--success)' : 'var(--text-primary)',
            padding: '12px 16px',
            background: anyTime ? 'rgba(0, 206, 201, 0.1)' : 'var(--bg-input)',
            border: anyTime ? '1.5px solid rgba(0, 206, 201, 0.4)' : '1.5px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            transition: 'all 0.2s',
            marginBottom: anyTime ? '0' : '20px',
          }}>
            <input
              id="any-time"
              type="checkbox"
              checked={anyTime}
              onChange={e => setAnyTime(e.target.checked)}
              style={{ width: '20px', height: '20px', accentColor: 'var(--success)', cursor: 'pointer' }}
            />
            I'm available any time
            <Tooltip text="Check this if you're flexible and can make any date or time work. You won't need to pick specific dates." />
          </label>

          {anyTime && (
            <p style={{ fontSize: '0.85rem', color: 'var(--success)', marginTop: '12px', textAlign: 'center' }}>
              You'll be counted as available for every time slot others select.
            </p>
          )}
        </div>

        {!anyTime && (
          <div className="card fade-in fade-in-delay-2" style={{ marginBottom: '20px' }}>
            <h2 style={{ fontSize: '1.1rem', marginBottom: '16px' }}>
              📅 {isEditing ? 'Edit Available Dates' : 'Select Your Available Dates'} <Tooltip text="Click on the dates you're available. Click again to deselect. You can pick dates across multiple months." />
            </h2>
            <Calendar
              selectedDates={selectedDates}
              onToggleDate={handleToggleDate}
            />
          </div>
        )}

        {!anyTime && slots.length > 0 && (
          <div className="card fade-in" style={{ marginBottom: '20px' }}>
            <h2 style={{ fontSize: '1.1rem', marginBottom: '16px' }}>
              ⏰ Set Time Ranges <Tooltip text="For each date, set the window of time you're free. The app will find where everyone overlaps." />
            </h2>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '16px' }}>
              For each date, set the hours {isEditing ? `${editingParticipant.name} is` : "you're"} available.
            </p>
            <TimeSlots
              slots={slots}
              onUpdateSlot={handleUpdateSlot}
              onRemoveSlot={handleRemoveSlot}
            />
          </div>
        )}

        {submitError && (
          <div style={{
            color: 'var(--danger)',
            padding: '12px',
            background: 'rgba(225, 112, 85, 0.1)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: '16px',
          }}>
            {submitError}
          </div>
        )}

        <button
          type="submit"
          className="btn-primary"
          disabled={submitting || (!anyTime && slots.length === 0)}
          style={{ width: '100%', padding: '14px', fontSize: '1rem' }}
        >
          {submitting
            ? 'Submitting...'
            : isEditing
              ? `Update ${editingParticipant.name}'s Availability${anyTime ? ' (Any Time)' : ` (${slots.length} date${slots.length !== 1 ? 's' : ''})`}`
              : anyTime
                ? 'Submit Availability (Any Time)'
                : `Submit Availability (${slots.length} date${slots.length !== 1 ? 's' : ''})`
          }
        </button>
      </form>
    </div>
  );
}
